// AdminPanel.jsx - Responsive admin panel with mobile support
import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/api';
import Sidebar from './Sidebar';
import ModulesView from '../admin/ModuleView';
import ModuleDetail from './ModuleDetail';
import UsersView from './UsersView';
import ProgressView from './ProgressView';
import LoadingSpinner from './LoadingSpinner';
import NotificationToast from './NotificationToast';

const AdminPanel = () => {
  const { user, isAdmin } = useAuth();
  
  // Navigation state
  const [currentView, setCurrentView] = useState('modules'); // Start with modules instead of analytics
  const [selectedModule, setSelectedModule] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Data states
  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [users, setUsers] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Auto-clear notifications
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch data based on current view
  useEffect(() => {
    fetchDataForView(currentView);
  }, [currentView]);

  const fetchDataForView = async (view) => {
    if (view === 'modules') {
      await fetchModules();
    } else if (view === 'users') {
      await fetchUsers();
    } else if (view === 'progress') {
      await Promise.all([fetchUsers(), fetchModules(), fetchUserProgress()]);
    }
  };

  const fetchModules = async () => {
    try {
      setLoading(true);
      const modulesData = await apiService.getModules();
      setModules(modulesData || []);
    } catch (err) {
      setError(`Error al cargar módulos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (moduleId) => {
    try {
      setLoading(true);
      const chaptersData = await apiService.getChapters(moduleId);
      setChapters(chaptersData || []);
    } catch (err) {
      setError(`Error al cargar capítulos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await apiService.getUsers();
      setUsers(usersData || []);
    } catch (err) {
      setError(`Error al cargar usuarios: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProgress = async () => {
    try {
      setLoading(true);
      const progressData = await apiService.getUserProgress();
      setUserProgress(progressData || {});
    } catch (err) {
      setError(`Error al cargar progreso de usuarios: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleSelect = async (module) => {
    setSelectedModule(module);
    setCurrentView('module-detail');
    await fetchChapters(module.id);
  };

  const handleBackToModules = () => {
    setSelectedModule(null);
    setCurrentView('modules');
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">No tienes permisos de administrador</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar 
        currentView={currentView} 
        onViewChange={setCurrentView}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">
            {currentView === 'modules' && 'Módulos'}
            {currentView === 'module-detail' && 'Detalle del Módulo'}
            {currentView === 'users' && 'Usuarios'}
            {currentView === 'progress' && 'Progreso'}
          </h1>
          <div className="w-6" /> {/* Spacer for alignment */}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8">
            {loading && <LoadingSpinner />}
            
            {currentView === 'modules' && !selectedModule && (
              <ModulesView 
                modules={modules}
                onModuleSelect={handleModuleSelect}
                onRefresh={fetchModules}
                setError={setError}
                setSuccess={setSuccess}
              />
            )}
            
            {currentView === 'module-detail' && selectedModule && (
              <ModuleDetail 
                module={selectedModule}
                chapters={chapters}
                onBack={handleBackToModules}
                onRefresh={() => fetchChapters(selectedModule.id)}
                setError={setError}
                setSuccess={setSuccess}
              />
            )}
            
            {currentView === 'users' && (
              <UsersView 
                users={users}
                onRefresh={fetchUsers}
                setError={setError}
                setSuccess={setSuccess}
              />
            )}
            
            {currentView === 'progress' && (
              <ProgressView 
                users={users}
                modules={modules}
                userProgress={userProgress}
                onRefresh={() => Promise.all([fetchUsers(), fetchModules(), fetchUserProgress()])}
                setError={setError}
              />
            )}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <NotificationToast 
        error={error}
        success={success}
        onClear={() => {
          setError(null);
          setSuccess(null);
        }}
      />
    </div>
  );
};

export default AdminPanel;